import { buildAnimatedHeading } from "../../HeroTitleAnimation.js";
import { pageRoute } from "../../../config/paths.js";

const REQUEST_COPY = {
  titleSegments: [
    { text: "Tell us what ", highlight: null },
    { text: "capacity", highlight: "primary" },
    { text: " you need", highlight: null },
  ],
  intro:
    "Share how many people you need and your budget range. We’ll collect role details next — one role at a time.",
  highlights: [
    {
      label: "Best for",
      text: "Teams that need embedded engineers without a long hiring cycle.",
    },
    {
      label: "What happens next",
      text: "Role-by-role requirements, then a Taldel conversation to confirm fit.",
    },
    {
      label: "Frontend-only",
      text: "This flow captures your request in the browser for now — no backend submit yet.",
    },
  ],
};

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;");
}

function renderHighlight({ label, text }) {
  return `
    <div class="contact__highlight">
      <h3 class="contact__highlight-label">${escapeHtml(label)}</h3>
      <p class="contact__highlight-text">${escapeHtml(text)}</p>
    </div>
  `;
}

function renderRequestStep() {
  return `
    <div class="staff-form__step is-active" data-staff-step="request">
      <div class="staff-form__step-head">
        <p class="staff-form__eyebrow">Step 1</p>
        <h3 class="staff-form__step-title">Staffing request</h3>
      </div>

      <label class="contact__field staff-form__field">
        <span class="staff-form__label">Number of resources required<span aria-hidden="true">*</span></span>
        <input
          type="number"
          name="resourceCount"
          min="1"
          max="20"
          step="1"
          value="1"
          required
          inputmode="numeric"
        />
      </label>

      <label class="contact__field staff-form__field">
        <span class="staff-form__label">Targeted Budget $ per month</span>
        <input
          type="number"
          name="budget"
          min="0"
          step="50"
          placeholder="e.g. 500"
          inputmode="decimal"
        />
      </label>

      <label class="contact__field staff-form__field">
        <span class="staff-form__label">Instructions</span>
        <textarea
          name="instructions"
          rows="5"
          placeholder="Enter instructions..."
        ></textarea>
      </label>

      <p class="staff-form__hint">Based on the number (role details will be requested)</p>
      <p class="staff-form__error" data-staff-error hidden></p>

      <button class="btn btn--primary contact__submit" type="button" data-staff-continue>
        Continue
      </button>
    </div>
  `;
}

function renderRoleStep(index, total) {
  const n = index + 1;
  return `
    <div class="staff-form__step" data-staff-step="role" data-role-index="${index}" hidden>
      <div class="staff-form__step-head">
        <p class="staff-form__eyebrow">Step ${n + 1} · Role ${n} of ${total}</p>
        <h3 class="staff-form__step-title">Role details</h3>
      </div>

      <label class="contact__field staff-form__field">
        <span class="staff-form__label">Role title<span aria-hidden="true">*</span></span>
        <input type="text" name="roleTitle" placeholder="e.g. Senior React Engineer" required />
      </label>

      <label class="contact__field staff-form__field">
        <span class="staff-form__label">Seniority<span aria-hidden="true">*</span></span>
        <select name="seniority" required>
          <option value="" disabled selected>Select seniority</option>
          <option value="junior">Junior</option>
          <option value="mid">Mid-level</option>
          <option value="senior">Senior</option>
          <option value="lead">Lead / Principal</option>
        </select>
      </label>

      <label class="contact__field staff-form__field">
        <span class="staff-form__label">Required skills<span aria-hidden="true">*</span></span>
        <input type="text" name="skills" placeholder="e.g. React, TypeScript, Node.js" required />
      </label>

      <label class="contact__field staff-form__field">
        <span class="staff-form__label">Engagement duration</span>
        <input type="text" name="duration" placeholder="e.g. 3–6 months" />
      </label>

      <label class="contact__field staff-form__field">
        <span class="staff-form__label">Role notes</span>
        <textarea name="roleNotes" rows="4" placeholder="Timezone, ownership, tools, must-haves..."></textarea>
      </label>

      <p class="staff-form__error" data-staff-error hidden></p>

      <div class="staff-form__actions">
        <button class="btn btn--ghost" type="button" data-staff-back>Back</button>
        <button class="btn btn--primary contact__submit" type="button" data-staff-continue>
          ${n === total ? "Submit request" : "Continue"}
        </button>
      </div>
    </div>
  `;
}

function renderSuccess(payload) {
  const roles = payload.roles || [];
  return `
    <div class="staff-form__success" data-staff-success>
      <p class="staff-form__eyebrow">Request captured</p>
      <h3 class="staff-form__step-title">Thanks — your staffing brief is ready</h3>
      <p class="staff-form__success-body">
        Frontend-only for now. You asked for <strong>${escapeHtml(payload.resourceCount)}</strong>
        resource${payload.resourceCount === 1 ? "" : "s"}
        ${payload.budget ? ` with a target near <strong>$${escapeHtml(payload.budget)}</strong>/mo` : ""}.
      </p>
      <ul class="staff-form__success-roles">
        ${roles
          .map(
            (role, i) => `
          <li>
            <strong>Role ${i + 1}:</strong>
            ${escapeHtml(role.roleTitle)} · ${escapeHtml(role.seniority)} · ${escapeHtml(role.skills)}
          </li>
        `
          )
          .join("")}
      </ul>
      <div class="staff-form__actions">
        <a class="btn btn--primary" href="${pageRoute("signup")}">Register</a>
      </div>
    </div>
  `;
}

export function createStaffAugmentationFormSection() {
  const section = document.createElement("section");
  section.id = "request";
  section.className = "section contact staff-form-section";

  section.innerHTML = `
    <div class="section__inner contact__layout reveal">
      <div class="contact__info">
        <h2 class="contact__title">${buildAnimatedHeading(REQUEST_COPY.titleSegments)}</h2>
        <p class="contact__intro">${REQUEST_COPY.intro}</p>
        <div class="contact__highlights">
          ${REQUEST_COPY.highlights.map(renderHighlight).join("")}
        </div>
      </div>

      <div class="contact__form staff-form" data-staff-form>
        ${renderRequestStep()}
        <div data-staff-role-host></div>
      </div>
    </div>
  `;

  bindStaffForm(section);
  return section;
}

function showError(stepEl, message) {
  const errorEl = stepEl.querySelector("[data-staff-error]");
  if (!errorEl) return;
  errorEl.hidden = !message;
  errorEl.textContent = message || "";
}

function bindSelectFill(root) {
  root.querySelectorAll("select").forEach((select) => {
    const sync = () => {
      select.classList.toggle("contact__select--filled", select.value !== "");
    };
    select.addEventListener("change", sync);
    sync();
  });
}

function readRoleFields(stepEl) {
  const data = new FormData();
  stepEl.querySelectorAll("input, select, textarea").forEach((el) => {
    if (el.name) data.set(el.name, el.value.trim());
  });
  return {
    roleTitle: data.get("roleTitle") || "",
    seniority: data.get("seniority") || "",
    skills: data.get("skills") || "",
    duration: data.get("duration") || "",
    roleNotes: data.get("roleNotes") || "",
  };
}

function validateRole(role) {
  if (!role.roleTitle) return "Role title is required.";
  if (!role.seniority) return "Please select seniority.";
  if (!role.skills) return "Required skills are required.";
  return "";
}

function bindStaffForm(section) {
  const formShell = section.querySelector("[data-staff-form]");
  const roleHost = section.querySelector("[data-staff-role-host]");
  const requestStep = section.querySelector('[data-staff-step="request"]');

  /** @type {{ resourceCount: number, budget: string, instructions: string, roles: object[] }} */
  const state = {
    resourceCount: 1,
    budget: "",
    instructions: "",
    roles: [],
  };

  let phase = "request"; // request | role | done
  let roleIndex = 0;

  const setActiveStep = (el) => {
    formShell.querySelectorAll("[data-staff-step]").forEach((step) => {
      const on = step === el;
      step.classList.toggle("is-active", on);
      step.hidden = !on;
    });
  };

  const buildRoleSteps = (count) => {
    roleHost.innerHTML = Array.from({ length: count }, (_, i) =>
      renderRoleStep(i, count)
    ).join("");
    bindSelectFill(roleHost);
  };

  const finish = () => {
    phase = "done";
    formShell.innerHTML = renderSuccess(state);
  };

  formShell.addEventListener("click", (event) => {
    const continueBtn = event.target.closest("[data-staff-continue]");
    const backBtn = event.target.closest("[data-staff-back]");

    if (backBtn) {
      if (phase !== "role") return;
      showError(formShell.querySelector(".staff-form__step.is-active"), "");
      if (roleIndex === 0) {
        phase = "request";
        roleHost.innerHTML = "";
        setActiveStep(requestStep);
        return;
      }
      roleIndex -= 1;
      const prev = roleHost.querySelector(`[data-role-index="${roleIndex}"]`);
      setActiveStep(prev);
      return;
    }

    if (!continueBtn) return;

    if (phase === "request") {
      showError(requestStep, "");
      const countInput = requestStep.querySelector('[name="resourceCount"]');
      const budgetInput = requestStep.querySelector('[name="budget"]');
      const instructionsInput = requestStep.querySelector('[name="instructions"]');

      if (!requestStep.checkValidity?.() && countInput && !countInput.checkValidity()) {
        countInput.reportValidity();
        return;
      }

      const count = Number(countInput?.value || 0);
      if (!Number.isFinite(count) || count < 1) {
        showError(requestStep, "Enter at least 1 resource.");
        countInput?.focus();
        return;
      }
      if (count > 20) {
        showError(requestStep, "Please keep the request to 20 resources or fewer.");
        countInput?.focus();
        return;
      }

      state.resourceCount = Math.floor(count);
      state.budget = String(budgetInput?.value || "").trim();
      state.instructions = String(instructionsInput?.value || "").trim();
      state.roles = Array.from({ length: state.resourceCount }, () => null);

      buildRoleSteps(state.resourceCount);
      phase = "role";
      roleIndex = 0;
      requestStep.classList.remove("is-active");
      requestStep.hidden = true;
      const firstRole = roleHost.querySelector('[data-role-index="0"]');
      setActiveStep(firstRole);
      firstRole?.querySelector("input, select, textarea")?.focus();
      return;
    }

    if (phase === "role") {
      const stepEl = roleHost.querySelector(`[data-role-index="${roleIndex}"]`);
      if (!stepEl) return;
      showError(stepEl, "");

      const role = readRoleFields(stepEl);
      const error = validateRole(role);
      if (error) {
        showError(stepEl, error);
        stepEl.querySelector(":invalid, input, select")?.focus?.();
        return;
      }

      // Native required check for empty selects/inputs
      const requiredEmpty = [...stepEl.querySelectorAll("[required]")].find(
        (el) => !String(el.value || "").trim()
      );
      if (requiredEmpty) {
        requiredEmpty.reportValidity?.();
        return;
      }

      state.roles[roleIndex] = role;

      if (roleIndex >= state.resourceCount - 1) {
        finish();
        return;
      }

      roleIndex += 1;
      const next = roleHost.querySelector(`[data-role-index="${roleIndex}"]`);
      setActiveStep(next);
      next?.querySelector("input, select, textarea")?.focus();
    }
  });

  bindSelectFill(formShell);
}
